import {ChatCompletionTool} from 'openai/resources';
import {z, ZodTypeAny} from 'zod';
import {concatToLoggerPrefix, logger, updateLoggerPrefix} from './logger';
import {LLMCallOptions} from '../copilot.types';
import getDefaultLLMModel from '../models/utils/getDefaultLLMModel';
import zodToJsonSchema from 'zod-to-json-schema';
import openAiApi from 'common/src/modules/openAi/openAi.api';
import {zodResponseFormat} from 'openai/helpers/zod';

async function invokeStandaloneLLMCall<T extends ZodTypeAny>({
  messages,
  responseSchema,
  buildResponseUsingToolCall = true,
  model,
  abortSignal,
}: LLMCallOptions<T>): Promise<z.infer<T>> {
  const {previousPrefix: previousLoggerPrefix} = concatToLoggerPrefix('standalone LLM call');

  try {
    model ??= getDefaultLLMModel();

    if (!model) {
      throw new Error('No model provided and no default model is set');
    }

    logger.log(
      'Standalone LLM call. messages:',
      messages,
      'buildResponseUsingToolCall:',
      buildResponseUsingToolCall,
      'model config:',
      model,
    );

    const constructResponseTool: ChatCompletionTool = {
      type: 'function',
      function: {
        name: 'construct-response',
        parameters: zodToJsonSchema(responseSchema),
      },
    };

    const startTime = performance.now();
    const aiResponse = await openAiApi.createChatCompletion(
      {
        ...model,
        messages,
        tools: buildResponseUsingToolCall ? [constructResponseTool] : [],
        response_format: buildResponseUsingToolCall
          ? undefined
          : zodResponseFormat(responseSchema, 'response'),
        tool_choice: buildResponseUsingToolCall
          ? {type: 'function', function: {name: constructResponseTool.function.name}}
          : undefined,
      },
      abortSignal,
    );
    const duration = performance.now() - startTime;
    logger.log(`Standalone LLM call completed in ${duration.toFixed(0)}ms`);

    let responseObject: z.infer<T>;

    if (buildResponseUsingToolCall) {
      const toolCall = aiResponse.choices[0].message.tool_calls?.[0];

      if (!toolCall || !toolCall.function.arguments) {
        throw new Error('No tool call found in LLM response');
      }

      responseObject = JSON.parse(toolCall.function.arguments);
    } else {
      const response = aiResponse.choices[0].message.content || '';
      responseObject = responseSchema.parse(JSON.parse(response));
    }

    logger.log('Standalone LLM call response:', responseObject);

    return responseObject;
  } finally {
    updateLoggerPrefix(previousLoggerPrefix);
  }
}

export default invokeStandaloneLLMCall;
